import type { ChangeEventHandler, FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import IconMdi from '@components/IconMdi';
import Input from '@components/Input';
import type { InputFieldProps } from '@components/InputField';
import { InputField } from '@components/InputField';
import { Show, ShowFirstMatching } from '@components/Show';
import { Spinner } from '@components/Spinner';
import { TooltipedIcon } from '@components/TooltipedIcon';
import { mdiCloseCircleOutline, mdiMagnify } from '@mdi/js';
import { isEmptyValue } from '@root/helpers';
import { useVirtualizer } from '@tanstack/react-virtual';
import sharedClasses from '@utils/shared-classes';
import clsx from 'clsx';

import type { UIOnChangeFn, UISelectionType } from './types';

export interface SearchableInputSelectOpenSearchProps extends InputFieldProps {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  dataTestId?: string;
  id?: string;
  initialSelectedItem?: string;
  isLoading?: boolean;
  labelText?: string;
  name: string;
  noOptionsLeftLabel?: string;
  onChange: UIOnChangeFn;
  options: UISelectionType[];
  placeholder?: string;
  value?: string;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  searchText: string;
  setSearchText: (searchText: string) => void;
  hasNextPage: boolean;
}

const INNER_CONTAINER_HEIGHT_PX = 398;

export const SearchableInputSelectOpenSearch: FC<SearchableInputSelectOpenSearchProps> = ({
  dataTestId,
  isDisabled,
  isLoading = false,
  name,
  noOptionsLeftLabel = 'No existing options available',
  onChange,
  options,
  placeholder,
  value,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  searchText: _searchText,
  setSearchText,
  ...props
}) => {
  'use memo';
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? options.length + 1 : options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= options.length - 1 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, options.length, isFetchingNextPage, rowVirtualizer, virtualItems]);

  // While the user is typing, `typed` holds their in-progress text; otherwise it is null and the
  // input shows the selected option's name. Derived (not synced into state) so it stays correct
  // across remounts and late-loading options without an effect.
  const [typed, setTyped] = useState<string | null>(null);
  const selectedName = options.find((item) => item.value === value)?.name ?? '';
  const displayValue = typed ?? selectedName;

  const resetSearch = () => {
    setTyped(null);
    setSearchText('');
  };

  const changeHandler: ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValue = event.currentTarget.value;
    setTyped(newValue);
    setSearchText(newValue);
  };

  return (
    <InputField {...props} isDisabled={isDisabled || isLoading}>
      <div
        className={clsx(
          'border-info-300 flex flex-col rounded-lg border shadow-xs',
          isDisabled && sharedClasses.disabled,
        )}
      >
        <div className="border-info-300 relative flex justify-center gap-2 border-b">
          <IconMdi path={mdiMagnify} size={0.75} className="text-info-400 absolute top-1/2 left-2 -translate-y-1/2" />
          <Input
            autoFocus
            className="border-none pl-8 focus:outline-hidden"
            key={value}
            name={name}
            onChange={changeHandler}
            placeholder={placeholder}
            value={displayValue}
            dataTestId={`${dataTestId}:search`}
          />
          <Show when={!isEmptyValue(value)}>
            <TooltipedIcon
              placement="top"
              path={mdiCloseCircleOutline}
              className="text-info-400 hover:text-error-500 mr-2 cursor-pointer transition-colors"
              size={0.625}
              content="Remove selection"
              onClick={() => {
                resetSearch();
                onChange(undefined, name);
              }}
            />
          </Show>
          <Show when={isLoading}>
            <Spinner className="[&>path]:fill-info-400 absolute top-3 right-3" size="xs" />
          </Show>
        </div>
        <ul className="relative flex h-44 flex-col gap-2 overflow-y-auto py-1 text-gray-700">
          <ShowFirstMatching>
            <Show when={isLoading}>
              <>
                {new Array(4).fill(null).map((_, idx) => (
                  <li className="flex animate-pulse px-3 py-2" key={`searchable-input-select-skeleton-${idx}`}>
                    <p className="h-4 w-3/4 rounded-lg bg-slate-200" />
                  </li>
                ))}
              </>
            </Show>
            <Show when={!isLoading}>
              <div
                ref={parentRef}
                className="w-full overflow-auto"
                style={{
                  height: `${INNER_CONTAINER_HEIGHT_PX}px`,
                }}
              >
                <div
                  className="relative w-full"
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > options.length - 1;
                    const option = options[virtualRow.index];

                    return (
                      <div
                        key={virtualRow.index}
                        className="absolute top-0 left-0 w-full"
                        style={{
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {isLoaderRow ? (
                          <div className="truncate px-3 py-2 text-sm leading-5 font-normal transition-colors">
                            {hasNextPage ? 'Loading more...' : 'Nothing more to load'}
                          </div>
                        ) : (
                          <li
                            className={clsx(
                              'hover:bg-info-100 cursor-pointer truncate px-3 py-2 text-sm leading-5 font-normal transition-colors',
                              option.value === value && 'bg-info-100',
                            )}
                            key={option.value}
                            onClick={() => {
                              // Stop editing so the input shows the selected option's name (derived).
                              setTyped(null);
                              onChange(option.value as string, name);
                            }}
                            data-testid={`${dataTestId}:option-${option.value}`}
                          >
                            {option.name}
                          </li>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <Show when={options.length === 0}>
                <span className="absolute inset-0 p-3 text-sm leading-5 font-normal text-gray-400">
                  {noOptionsLeftLabel}
                </span>
              </Show>
            </Show>
          </ShowFirstMatching>
        </ul>
      </div>
    </InputField>
  );
};
